import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { ATAJOS_VISOR, BOTONES_VISOR } from "./visorArchivo";

/**
 * Ayuda de la pestaña Evidencias del caso. Lo que antes iba en su «?» con
 * globito y en los tooltips de los archivos, del chip de compresión y del
 * switch «Comprimir videos». Los botones de edición salen solo para admin y
 * soporte, igual que en la pestaña.
 */
export function ayudaEvidencias({ puedeEditar }: { puedeEditar: boolean }): ContenidoAyuda {
  const si = (items: ItemAyuda[]) => (puedeEditar ? items : []);
  return {
    manual: "zip",
    intro: (
      <p>
        Los archivos del caso: fotos, videos, logs, PDFs. Desde aquí se arman los paquetes ZIP
        para mandar al proveedor.
      </p>
    ),
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "☰ Lista / ▦ Cuadrícula",
            text: "Lista compacta (una fila por archivo, ideal cuando hay muchos) o cuadrícula con miniaturas grandes. Tu elección se recuerda en este navegador.",
          },
          ...si([
            {
              label: "+ Generar paquete ZIP",
              text: "Eliges archivos y arma un ZIP para el proveedor, cifrado con contraseña o «Sin contraseña». La contraseña siempre son los últimos 6 dígitos del TE (el cliente lo ve en el asunto del correo). Si lo asignas a un IMEI, solo cambia el nombre del ZIP. El enlace de descarga dura 90 días. Sale gris si el caso no tiene archivos.",
            },
            {
              label: "Próximas subidas →",
              text: "A qué IMEI se asignan los archivos que subas desde ahora, o «General del caso». Después puedes cambiar cada archivo.",
            },
            {
              label: "Comprimir videos",
              text: "Los videos de más de 100 MB siempre se comprimen; este switch decide si los de 100 MB o menos también. Se recuerda en este navegador.",
            },
            {
              label: "Recuadro punteado",
              text: "Arrastra archivos ahí, o tócalo para elegirlos. Hasta 200 MB por archivo (en un video cuenta el tamaño ya comprimido).",
            },
          ]),
          {
            label: "Miniatura de un archivo",
            text: "Tócala para ver el archivo sin descargarlo. En un video es un cuadro real del clip; en un PDF, Word o Excel, su primera página. No abre si el archivo ya se borró por retención, si el video se está comprimiendo o si su compresión falló.",
          },
          ...si([
            {
              label: "«— sin IMEI —» (en cada archivo)",
              text: "Asocia el archivo a un equipo del caso, para que el proveedor sepa de cuál es.",
            },
            {
              label: "🔗",
              text: "Copia un enlace público al archivo (firmado, dura 90 días) que se abre directo en el navegador, sin cuenta ni contraseña: para pegarlo en el ticket de un proveedor. Solo en archivos listos.",
            },
            {
              label: "×",
              text: "Elimina el archivo, después de confirmar: se borra del servidor y del caso. Los ZIP que ya lo incluían siguen funcionando.",
            },
            {
              label: "↻ Reintentar / Descartar",
              text: "Cuando un video no se pudo comprimir: Reintentar lo vuelve a intentar con el original, que sigue guardado; Descartar lo borra del caso, original incluido, después de confirmar.",
            },
            {
              label: "Descartar aviso",
              text: "En un video que no se guardó por pesado: borra su registro del caso. No pregunta porque no se pierde nada: el archivo ya no estaba.",
            },
          ]),
          {
            label: "▾ / ▸ Paquetes generados",
            text: "Pliega o despliega la tarjeta de los ZIP. Plegada sigue mostrando la contraseña y el último ZIP, y se recuerda en todos los casos. Al generar un ZIP nuevo se abre sola.",
          },
          { label: "Copiar (contraseña)", text: "Copia la contraseña de los ZIP del caso." },
          { label: "Copiar enlace", text: "Copia el enlace de descarga de ese ZIP." },
          ...si([
            {
              label: "🗑 Eliminar (un ZIP)",
              text: "Borra el ZIP y su enlace deja de funcionar de inmediato, sin esperar a que caduque. Pide confirmación y no se puede deshacer.",
            },
          ]),
          ...BOTONES_VISOR,
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          { label: "Comprimiendo…", text: "El video se está comprimiendo; se actualiza solo." },
          {
            label: "🗜️ 120 MB → 18 MB · −85%",
            text: "Video ya comprimido, para verse rápido en cualquier navegador y pesar menos: peso original → peso final.",
          },
          { label: "✕ Error al comprimir", text: "No se pudo comprimir: usa Reintentar o Descartar." },
          {
            label: "⚠ Muy pesado (>200 MB)",
            text: "Aun comprimido pasa de 200 MB, así que no se guardó aquí: súbelo por otro medio.",
          },
          {
            label: "Se elimina en …",
            text: "Cuánto le falta al archivo para borrarse por retención. En ámbar cuando faltan menos de 30 días.",
          },
          {
            label: "Archivo eliminado por retención",
            text: "El archivo se borró del servidor al cumplir su plazo; el registro (nombre, fecha, quién lo subió) se queda en el caso.",
          },
          {
            label: "N descargas ✓",
            text: "Cuántas veces se bajó el ZIP; la ✓ dice que alguien ya lo descargó.",
          },
          { label: "EXPIRADO / Expira …", text: "El enlace del ZIP ya caducó, o la fecha en que caduca." },
          {
            label: "Contraseña (anterior) / Sin contraseña (ZIP plano)",
            text: "Cuando los ZIP del caso no comparten contraseña, cada uno muestra la suya.",
          },
        ],
      },
      {
        titulo: "Cuánto se guardan",
        cuerpo: (
          <p>
            Los archivos se borran del servidor 18 meses después de subirse; los ZIP, 12 meses
            después de que se cierra el caso (mientras siga abierto se conservan). El registro se
            queda en el caso.
          </p>
        ),
      },
      { titulo: "Atajos de teclado", items: ATAJOS_VISOR },
    ],
  };
}
